import React, { useEffect, useState } from "react";
import { Modal, Form, Select, DatePicker, InputNumber, Alert } from "antd";
import dayjs from "dayjs";
import api from "../../api";
import { PRODUCT_TYPE_OPTIONS } from "../../constants";
import { useToast } from "../../components/toast";

export default function PromotionFormModal({
  open,
  onClose,
  editingRow,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const toast = useToast();

  const isEdit = !!editingRow;
  const productType = Form.useWatch("productType", form);

  // Edit үед утга оруулах
  useEffect(() => {
    if (open && editingRow) {
      form.setFieldsValue({
        ...editingRow,
        startsAt: editingRow.startsAt ? dayjs(editingRow.startsAt) : null,
        endsAt: editingRow.endsAt ? dayjs(editingRow.endsAt) : null,
      });
    } else if (open) {
      form.resetFields();
      setProductOptions([]);
    } else {
      // Modal хаагдахад productType-с хамааран productId цэвэрлэх
      form.setFieldValue("productId", undefined); // ✅ зөвхөн create flow-д хэрэгтэй
    }
  }, [open, editingRow]);

  // productType өөрчлөгдөхөд бүтээгдэхүүн жагсаалт татах
  useEffect(() => {
    if (!productType) {
      setProductOptions([]);
      return;
    }

    // ✅ Edit биш үед л productId цэвэрлэнэ
    if (!isEdit) {
      form.setFieldValue("productId", undefined);
    }

    setLoadingProducts(true);

    const fetchMap = {
      CARD: () => api.cards_list({ limit: 100 }),
      LOAN: () => api.loans_list({ limit: 100 }),
      SAVINGS: () => api.savings_list({ limit: 100 }),
      INSURANCE: () => api.insurances_list({ limit: 100 }),
      APARTMENT: () => api.apartments_list({ limit: 100 }),
    };

    fetchMap[productType]?.().then((res) => {
      if (res?.status === 200) {
        setProductOptions(
          res.data.data.map((item) => ({
            value: item.id,
            label: `${item.issuer?.name ? `[${item.issuer.name}] ` : ""}${item.name}`,
          })),
        );
      }
      setLoadingProducts(false);
    });
  }, [productType]);

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);

    const payload = {
      ...values,
      startsAt: values.startsAt?.toISOString(),
      endsAt: values.endsAt?.toISOString(),
    };

    const res = isEdit
      ? await api.promotions_update(editingRow.id, payload)
      : await api.promotions_create(payload);

    if (res?.status === 200 || res?.status === 201) {
      onSuccess(res.data, isEdit);
      onClose();
    } else {
      toast(res.data.message || "Алдаа гарлаа", "error");
    }
    setSaving(false);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onSave}
      confirmLoading={saving}
      title={isEdit ? "Promotion засах" : "Promotion нэмэх"}
      okText={isEdit ? "Хадгалах" : "Нэмэх"}
      cancelText="Болих"
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* Бүтээгдэхүүний төрөл */}
        <Form.Item
          name="productType"
          label="Бүтээгдэхүүний төрөл"
          rules={[{ required: true, message: "Төрөл сонгоно уу" }]}
        >
          <Select
            placeholder="Карт / Зээл..."
            options={PRODUCT_TYPE_OPTIONS}
            disabled={isEdit}
          />
        </Form.Item>

        {/* Бүтээгдэхүүн */}
        <Form.Item
          name="productId"
          label="Бүтээгдэхүүн"
          rules={[{ required: true, message: "Бүтээгдэхүүн сонгоно уу" }]}
        >
          <Select
            placeholder={productType ? "Хайх..." : "Эхлээд төрөл сонгоно уу"}
            options={productOptions}
            loading={loadingProducts}
            disabled={!productType || isEdit}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {/* Огноо */}
        <div style={{ display: "flex", gap: 12 }}>
          <Form.Item
            name="startsAt"
            label="Эхлэх огноо"
            rules={[{ required: true, message: "Огноо сонгоно уу" }]}
            style={{ flex: 1 }}
          >
            <DatePicker
              style={{ width: "100%" }}
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
          <Form.Item
            name="endsAt"
            label="Дуусах огноо"
            rules={[{ required: true, message: "Огноо сонгоно уу" }]}
            style={{ flex: 1 }}
          >
            <DatePicker
              style={{ width: "100%" }}
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
        </div>

        {/* Эрэмбэ */}
        <Form.Item
          name="priority"
          label="Эрэмбэ"
          initialValue={0}
          tooltip="Ижил байршилд хэд байвал их тоо нь эхэнд харагдана"
        >
          <InputNumber min={0} max={100} style={{ width: "100%" }} />
        </Form.Item>

        {isEdit && !editingRow?.isActive && (
          <Alert
            type="warning"
            showIcon
            message={`Зогсоосон шалтгаан: ${editingRow.cancelReason || "—"}`}
            style={{ marginTop: 4 }}
          />
        )}
      </Form>
    </Modal>
  );
}
